<!-- 組織圖的一個人（遞迴）：左邊是卡片，右邊是他的下線，用線連起來 -->
<template>
  <div class="ocn">
    <div
      class="ocn__card"
      :class="{ 'ocn__card--root': node.isRoot, 'ocn__card--hit': hit, 'ocn__card--group': hasChildren && !node.isRoot, 'ocn__card--open': hasChildren && open }"
      :role="hasChildren && !node.isRoot ? 'button' : null"
      :tabindex="hasChildren && !node.isRoot ? 0 : null"
      :aria-expanded="hasChildren && !node.isRoot ? String(open) : null"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span class="ocn__avatar" :style="avatar">{{ initial }}</span>
      <div class="ocn__text">
        <div class="ocn__name" v-html="name"></div>
        <span v-if="node.grade || !node.isRoot" class="ocn__grade" :class="{ 'ocn__grade--member': plain }">{{ node.grade || "一般會員" }}</span>
      </div>

      <!-- 收合時顯示有幾個直屬下線，展開時是減號 -->
      <span v-if="hasChildren && !node.isRoot" class="ocn__toggle" :class="{ 'ocn__toggle--open': open }">
        <v-icon v-if="open" size="14">mdi-minus</v-icon>
        <template v-else>{{ node.children.length }}</template>
      </span>
    </div>

    <div v-if="hasChildren && open" class="ocn__children">
      <div v-for="c in node.children" :key="c.guid" class="ocn__child">
        <OrgChartNode :node="c" :depth="depth + 1" />
      </div>
    </div>
  </div>
</template>

<script>
import { avatarStyle, highlight, initialOf, isPlainMember } from "./orgUtils";

export default {
  name: "OrgChartNode",
  inject: ["orgChart"],
  props: {
    node: { type: Object, required: true },
    // 我 = -1，直屬 = 0
    depth: { type: Number, default: 0 },
  },
  computed: {
    hasChildren() {
      return Array.isArray(this.node.children) && this.node.children.length > 0;
    },
    open() {
      return this.orgChart.isOpen(this.node, this.depth);
    },
    hit() {
      return this.orgChart.isHit(this.node);
    },
    name() {
      return highlight(this.node.name, this.node.isRoot ? "" : this.orgChart.q);
    },
    plain() {
      return isPlainMember(this.node.grade);
    },
    initial() {
      return initialOf(this.node.name);
    },
    avatar() {
      return avatarStyle(this.node.name, this.node.unnamed);
    },
  },
  methods: {
    toggle() {
      if (this.hasChildren) this.orgChart.toggle(this.node, this.depth);
    },
  },
};
</script>

<style scoped>
/*
  卡片在左、下線在右。卡片對齊第一個下線那一列（不是整欄的正中間）——
  直屬一多，置中的話「我」會掉到很下面，一打開看不到自己。

  卡片固定高 58px，所有連接線都接在卡片的中線（29px）。
  不能用 50%：展開的下線那一格會跟著他自己的下線變高，50% 就不在卡片中間了。
*/
.ocn {
  display: flex;
  align-items: flex-start;
}

.ocn__card {
  position: relative;
  flex-shrink: 0;
  height: 58px;
  /* 寬度跟著內容，位階字長的（創業中心代理商）也放得下 */
  min-width: 128px;
  max-width: 184px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e3e1ee;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(30, 20, 80, 0.05);
}

.ocn__card--group {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.ocn__card--group:active {
  background: #f6f5fb;
}

.ocn__card--group:focus-visible {
  outline: 2px solid #7c4dff;
  outline-offset: 2px;
}

/* 我：紫色外框 */
.ocn__card--root {
  border: 2px solid #7c4dff;
  box-shadow: 0 2px 8px rgba(124, 77, 255, 0.18);
}

/* 搜尋比對到的人 */
.ocn__card--hit {
  background: #fffbe6;
  border-color: #f5c400;
}

.ocn__avatar {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
}

.ocn__text {
  min-width: 0;
  flex: 1 1 auto;
}

.ocn__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ocn__grade {
  display: inline-block;
  max-width: 100%;
  margin-top: 3px;
  padding: 0 7px;
  border-radius: 999px;
  background: #157af0;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ocn__grade--member {
  background: #eeeeee;
  color: rgba(0, 0, 0, 0.6);
}

/* 展開／收合鈕：壓在卡片右邊的連接線上 */
.ocn__toggle {
  position: absolute;
  right: -11px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: #7c4dff;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.ocn__toggle--open {
  background: #fff;
  color: #7c4dff;
  border: 1.5px solid #7c4dff;
}

.ocn__toggle .v-icon {
  color: inherit;
}

/*
  連接線：
  卡片 ───┬─ 下線 1
          ├─ 下線 2
          └─ 下線 3
*/
.ocn__children {
  display: flex;
  flex-direction: column;
  padding-left: 14px;
}

/* 展開的卡片：從卡片右邊拉一小段到直線 */
.ocn__card--open::after {
  content: "";
  position: absolute;
  left: 100%;
  top: 50%;
  width: 15px;
  border-top: 1.5px solid #bdb8d6;
}

/* 上面不留空，第一個下線的卡片才跟上一層的卡片同一條中線；人跟人之間的距離放在下面 */
.ocn__child {
  position: relative;
  padding: 0 0 10px 14px;
}

/* 直線：從第一個人的中間連到最後一個人的中間 */
.ocn__child::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 1.5px solid #bdb8d6;
}

.ocn__child:first-child::before {
  top: 29px;
}

.ocn__child:last-child::before {
  bottom: auto;
  height: 29px;
}

.ocn__child:only-child::before {
  display: none;
}

/* 直線到每個人的那一小段 */
.ocn__child::after {
  content: "";
  position: absolute;
  left: 0;
  top: 29px;
  width: 14px;
  border-top: 1.5px solid #bdb8d6;
}

::v-deep mark {
  background: #ffe066;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}
</style>
